import { DashboardT } from 'business/definitions';
import { useQuery } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';

export const GET_DASHBOARD_FILTERS_CACHE_KEY = 'getDashboardFilters';

export const getForceDefaultFilters = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    return params.get('force_default_filters') === 'true';
};

/**
 * Constructs the endpoint URL for fetching dashboard filters.
 * @param dashboardSlug - The slug of the dashboard.
 * @param publicToken - Optional public token for access.
 * @returns The constructed endpoint URL.
 */
const getDashboardFiltersEndpoint = (
    dashboardSlug: string | undefined,
    publicToken?: string | null,
): string => {
    const basePath = `explore/dashboard/${dashboardSlug}/filters`;
    const params = new URLSearchParams();
    if (publicToken) {
        params.set('public_token', publicToken);
    }
    if (getForceDefaultFilters()) {
        params.set('force_default_filters', 'true');
    }
    const paramsString = params.toString();
    return paramsString ? `${basePath}?${paramsString}` : basePath;
};

const useGetDashboardFilters = (
    dashboard: DashboardT | undefined,
    publicToken?: string | null,
) => {
    const dashboardKey = dashboard?.key;
    const dashboardSlug = dashboard?.slug;
    const endpoint = getDashboardFiltersEndpoint(dashboardSlug, publicToken);

    return useQuery({
        queryKey: [
            GET_DASHBOARD_FILTERS_CACHE_KEY,
            dashboardKey,
            getForceDefaultFilters(),
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<DashboardFilterT[]>({
                endpoint,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled: !!dashboardKey && !!dashboardSlug,
    });
};

export default useGetDashboardFilters;
